import classNames from 'classnames';
import React from 'react';
import Typography from '@/components/atoms/typography/typography';
import FavoriteHeart from '@/components/organisms/favorite-heart/favorite-heart';
import Link from '@/components/utilities/link/link';
import { useFavoritesContext } from '@/libraries/contexts/favorites-context';
import { useHotelContext } from '@/libraries/contexts/hotel-context/hotel-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { usePredefinedSearchContext } from '@/libraries/contexts/predefined-search-context/predefined-search-context';
import { useSearchContext } from '@/libraries/contexts/search-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './configuration-flow-footer.module.scss';

export interface ConfigurationFlowFooterProps {
  /** The content to render as a React node */
  children?: React.ReactNode;
  /** Additional class names */
  className?: string;
  /** Whether the step is the first step */
  isFirstStep: boolean;
  /** The total price for the offer */
  totalPrice?: number;
}

const ConfigurationFlowFooter = ({
  children,
  className,
  isFirstStep,
  totalPrice,
}: ConfigurationFlowFooterProps) => {
  const { t } = useI18n();
  const currencyFormatter = useCurrencyFormatter();
  const { uniqueId } = useHotelContext();
  const { addFavoriteOffer, removeFavoriteOffer, isFavorite } =
    useFavoritesContext();
  const { dateFormatter } = useDateFormatter();
  const { setIsClickedEditSearch } = useSearchContext();
  const { predefinedSearchParameters } = usePredefinedSearchContext();
  const { searchDetails } = usePageDetailsContext();
  const isInPredefinedSearchPage = Boolean(predefinedSearchParameters);

  const getDurationLabel = (
    periodType: Content.Components['PeriodType'],
  ): string => {
    const durationKeys: Record<Content.Components['PeriodType'], string> = {
      all: 'items.common.periodType.all',
      custom: 'items.common.periodType.custom',
      miniWeek: 'items.common.periodType.miniWeek',
      threeWeeks: 'items.common.periodType.threeWeeks',
      twoWeeks: 'items.common.periodType.twoWeeks',
      week: 'items.common.periodType.week',
    };

    return t(durationKeys[periodType]);
  };

  const detailsString = searchDetails
    ? [
        getDurationLabel(searchDetails.periodType),
        searchDetails.departure,
        searchDetails.travelers,
        [
          searchDetails.outboundDepartureDate &&
            dateFormatter(searchDetails.outboundDepartureDate).format(
              'D MMM YYYY',
            ),
          searchDetails.homeboundDepartureDate &&
            dateFormatter(searchDetails.homeboundDepartureDate).format(
              'D MMM YYYY',
            ),
        ].join(` ${t('items.common.words.to')} `),
      ].join(', ')
    : '';

  const handleClickFavorite = () => {
    if (!uniqueId) {
      return;
    }

    if (isFavorite(uniqueId)) {
      removeFavoriteOffer(uniqueId);
    } else {
      addFavoriteOffer(uniqueId);
    }
  };

  return (
    <div className={classNames(styles.component, className)}>
      <div className={styles.contentWrapper}>
        {isFirstStep && !isInPredefinedSearchPage && (
          <div className={styles.searchDetails}>
            <div className={styles.searchDetailsText}>
              <Typography tagStyle="bodyMedium">{detailsString}</Typography>
            </div>

            <Link
              className={styles.editSearchLink}
              onClick={() => setIsClickedEditSearch(true)}
            >
              {t('items.common.words.edit')}
            </Link>
          </div>
        )}

        <div className={styles.buttonContainer}>
          {totalPrice && (
            <div className={styles.priceSummary}>
              <Typography tagStyle="bodyMedium">
                {t('items.common.words.totalPrice')}
              </Typography>

              <Typography tagStyle="headingMedium">
                {`${currencyFormatter(totalPrice)}`}
              </Typography>
            </div>
          )}

          {children}
        </div>
      </div>

      <div className={styles.favoritesSuggestion}>
        <Typography tagStyle="bodyMedium">
          {t('items.modules.configurationFlow.readyToBook')}
        </Typography>

        <Typography tagStyle="bodyMedium">
          {t('items.favorites.addToFavorites')}
        </Typography>

        <FavoriteHeart
          isFavorite={uniqueId ? isFavorite(uniqueId) : false}
          onClickFavorite={() => handleClickFavorite()}
        />
      </div>
    </div>
  );
};

ConfigurationFlowFooter.displayName = 'ConfigurationFlowFooter';

export default ConfigurationFlowFooter;
